import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Snackbar,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { Add as AddIcon, FolderSpecial as ProjectIcon } from "@mui/icons-material";
import ProjectPicker from "../../pickers/ProjectPicker";
import QuotePicker from "../../pickers/QuotePicker";
import RFTestReportsList from "./RFTestReportsList";
import RFTestReportFormDialog from "./RFTestReportFormDialog";
import RFTestReportViewDialog from "./RFTestReportViewDialog";
import DeleteReportDialog from "./DeleteReportDialog";
import ReportService from "../../../services/Report.service";

export default function RFTestReport() {
    const [linkedTo, setLinkedTo] = useState("project"); // "project" | "quote"
    const [project, setProject] = useState(null);
    const [quote, setQuote] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [formDialog, setFormDialog] = useState({ open: false, item: null });
    const [viewDialog, setViewDialog] = useState({ open: false, item: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, report: null });

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const selectedItem = linkedTo === "project" ? project : quote;

    const refresh = useCallback(async () => {
        if (!selectedItem) return;
        setLoading(true);
        setError("");
        try {
            const res = await ReportService.listRFTestReports(
                linkedTo === "project"
                    ? { project: selectedItem.id }
                    : { quote: selectedItem.id },
            );
            setReports(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError("Failed to load RF Test Reports.");
        } finally {
            setLoading(false);
        }
    }, [linkedTo, selectedItem]);

    useEffect(() => {
        if (selectedItem) {
            refresh();
        } else {
            setReports([]);
        }
    }, [selectedItem, refresh]);

    const handleLinkedToChange = (_, val) => {
        if (!val) return;
        setLinkedTo(val);
        setProject(null);
        setQuote(null);
    };

    const handleSaved = () => {
        refresh();
        showSnackbar(formDialog.item ? "Report updated." : "Report created.");
    };

    const handleDeleted = () => {
        refresh();
        showSnackbar("Report deleted.", "info");
    };

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <ToggleButtonGroup
                    value={linkedTo}
                    exclusive
                    onChange={handleLinkedToChange}
                    size="small"
                    sx={{ flexShrink: 0 }}
                >
                    <ToggleButton value="project">Project</ToggleButton>
                    <ToggleButton value="quote">Quote</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }}>
                    {linkedTo === "project" ? (
                        <ProjectPicker value={project} onChange={setProject} size="small" />
                    ) : (
                        <QuotePicker value={quote} onChange={setQuote} size="small" />
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormDialog({ open: true, item: null })}
                    sx={{ flexShrink: 0, height: 40 }}
                >
                    New RF Test Report
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!selectedItem ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <ProjectIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">
                        Select a {linkedTo} above to view its RF Test Reports.
                    </Typography>
                </Box>
            ) : (
                <RFTestReportsList
                    reports={reports}
                    loading={loading}
                    onView={(report) => setViewDialog({ open: true, item: report })}
                    onEdit={(report) => setFormDialog({ open: true, item: report })}
                    onDelete={(report) => setDeleteDialog({ open: true, report })}
                />
            )}

            <RFTestReportFormDialog
                open={formDialog.open}
                onClose={() => setFormDialog({ open: false, item: null })}
                item={formDialog.item}
                initialProject={project}
                initialQuote={quote}
                initialLinkedTo={linkedTo}
                onSaved={handleSaved}
            />

            <RFTestReportViewDialog
                open={viewDialog.open}
                onClose={() => setViewDialog({ open: false, item: null })}
                report={viewDialog.item}
            />

            <DeleteReportDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, report: null })}
                report={deleteDialog.report}
                onDeleted={handleDeleted}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
